import { istPfand } from './pfand';
import type { Position, Steuersatz } from './warenkorb';

/**
 * Rabatt auf den ganzen Bon: je Steuersatz eine negative Position
 * „Rabatt n %" (RKSV: negative Beträge sind zulässig, der Steuersatz muss
 * stimmen – deshalb je Satz getrennt). Reine Cent-Rechnung, kaufmännisch
 * gerundet. Bestehende Rabattzeilen werden vorher entfernt.
 */
export const RABATT_PRAEFIX = 'Rabatt ';
export const RABATT_STUFEN: readonly number[] = [5, 10, 15, 20, 25, 50];

export function istRabatt(p: Position): boolean {
  return p.bezeichnung.startsWith(RABATT_PRAEFIX) && p.bruttoCent < 0;
}

export function ohneRabatt(positionen: readonly Position[]): Position[] {
  return positionen.filter((p) => !istRabatt(p));
}

export function mitRabatt(positionen: readonly Position[], prozent: number): Position[] {
  if (!Number.isInteger(prozent) || prozent <= 0 || prozent > 100) {
    throw new RangeError('Rabatt muss eine ganze Zahl zwischen 1 und 100 sein');
  }
  const basis = ohneRabatt(positionen);
  const jeSatz = new Map<Steuersatz, number>();
  for (const p of basis) {
    if (istPfand(p)) {
      continue; // Pfand und Leergut sind kein Preisnachlass-Gegenstand
    }
    jeSatz.set(p.steuersatz, (jeSatz.get(p.steuersatz) ?? 0) + p.bruttoCent);
  }
  const rabatte: Position[] = [];
  for (const [steuersatz, summe] of jeSatz) {
    if (summe <= 0) {
      continue;
    }
    const betrag = Math.round((summe * prozent) / 100);
    if (betrag > 0) {
      rabatte.push({
        bezeichnung: `${RABATT_PRAEFIX}${String(prozent)} %`,
        menge: 1,
        bruttoCent: -betrag,
        steuersatz,
      });
    }
  }
  return [...basis, ...rabatte];
}

/** Aktueller Rabattsatz des Bons (aus der Rabattzeile), sonst null. */
export function rabattProzent(positionen: readonly Position[]): number | null {
  const zeile = positionen.find(istRabatt);
  if (zeile === undefined) {
    return null;
  }
  const n = Number(/(\d+) %/.exec(zeile.bezeichnung)?.[1]);
  return Number.isInteger(n) ? n : null;
}

/**
 * Bon ändern (Menge, Zeilenpreis, Zeile entfernen) und einen bestehenden Bon-Rabatt
 * auf die neue Summe nachziehen – sonst stünde „Rabatt 10 %“ am Bon, gerechnet aber
 * mit dem alten Betrag.
 */
export function aendereMitRabatt(
  positionen: readonly Position[],
  aendern: (bon: readonly Position[]) => Position[],
): Position[] {
  const prozent = rabattProzent(positionen);
  const neu = aendern(positionen);
  return prozent === null ? neu : mitRabatt(neu, prozent);
}

/**
 * Kundenwechsel: Der Rabatt des bisherigen Kunden fällt (nur wenn er noch unverändert am
 * Bon steht – ein vom Bediener gesetzter anderer Satz bleibt), der neue Kundenrabatt gilt.
 * Bei Gutscheinen im Bon (Verkauf oder Einlösung) wird kein Rabatt gesetzt – dieselbe
 * Regel wie für die Rabatt-Taste.
 */
export function nachKundenwechsel(
  positionen: readonly Position[],
  bisherigerKundenRabatt: number | null,
  neuerKundenRabatt: number | null,
  gutscheineImBon: boolean,
): readonly Position[] {
  let ergebnis = positionen;
  if (bisherigerKundenRabatt !== null && rabattProzent(ergebnis) === bisherigerKundenRabatt) {
    ergebnis = ohneRabatt(ergebnis);
  }
  if (neuerKundenRabatt !== null && !gutscheineImBon && ohneRabatt(ergebnis).length > 0) {
    ergebnis = mitRabatt(ergebnis, neuerKundenRabatt);
  }
  return ergebnis;
}

/** Rabattsatz für eine hinzugefügte Position: bestehender Bon-Rabatt, sonst Kundenrabatt – nie bei Gutscheinen. */
export function rabattFuerNeuePosition(
  positionen: readonly Position[],
  kundenRabatt: number | null,
  gutscheineImBon: boolean,
): number | null {
  if (gutscheineImBon) {
    return null;
  }
  return rabattProzent(positionen) ?? kundenRabatt;
}
